import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Table, Tooltip } from 'antd';
import { Download, Search } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { useTp } from './tpStore';
import { LEAVE_KIND, branchById, currentContract, fmtD, isWorking, leaveBalance, normText, positionById, toDay } from './hrLogic';
import { EmpCell, PageHeader, exportXlsx } from './hrUi';
import { BranchSelect } from './tpUi';

/** Phép năm từng người: được hưởng (12 ngày + thâm niên, theo tháng làm việc), chuyển từ năm trước, đã nghỉ, đang chờ duyệt, còn lại */
export default function LeaveBalance() {
  const s = useHr();
  const t = useTp();
  const [params, setParams] = useSearchParams();
  const go = (p: Record<string, string | null>) => {
    const n = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const today = toDay();
  const branchId = params.get('branch') || '';
  const q = params.get('q') || '';
  const rows = useMemo(() => s.employees
    .filter(e => isWorking(e, today) && (!branchId || e.branchId === branchId) && (!q || normText(`${e.name} ${e.code}`).includes(normText(q))))
    .map(e => {
      const b = leaveBalance(s, e, today);
      const pending = t.requests.filter(r => r.empId === e.id && r.kind === 'leave' && r.leaveKind === 'annual' && r.status === 'pending').reduce((n, r) => n + (r.days || 0), 0);
      const next = (e.leaveRecords || []).filter(r => r.from > today).sort((a, b2) => a.from.localeCompare(b2.from))[0];
      return { e, b, pending, next, kind: currentContract(s, e.id, today)?.kind };
    })
    .sort((a, b) => a.b.remain - b.b.remain), [s, t.requests, branchId, q, today]);

  const doExport = () => exportXlsx(`phep_nam_${today.slice(0, 4)}`, 'Phép năm', rows.map(r => ({
    'Mã NS': r.e.code, 'Họ tên': r.e.name, 'Chi nhánh': branchById(s, r.e.branchId)?.short || '', 'Vị trí': positionById(s, r.e.positionId)?.name || '',
    'Được hưởng': r.b.entitled, 'Thâm niên': r.b.seniority, 'Năm trước chuyển sang': r.b.carried, 'Đã nghỉ': r.b.used, 'Chờ duyệt': r.pending, 'Còn lại': r.b.remain,
  })));

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Phép năm" desc={`Năm ${today.slice(0, 4)}: 12 ngày + 1 ngày mỗi 5 năm thâm niên, tính theo số tháng làm việc. Nghỉ ghi nhận từ đơn được duyệt và hồ sơ nhân sự`}
          extra={<Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button>} />
        <div className="flex flex-wrap gap-2">
          <BranchSelect value={branchId} onChange={v => go({ branch: v || null })} />
          <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, mã" className="!w-[200px]" value={q} onChange={e => go({ q: e.target.value || null })} />
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="middle" rowKey={r => r.e.id} dataSource={rows} scroll={{ x: 980 }} pagination={{ pageSize: 25, showSizeChanger: false, showTotal: n => `${n} nhân sự` }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có nhân sự" /> }}
            columns={[
              { title: 'Nhân sự', key: 'e', width: 260, render: (_, r) => <EmpCell s={s} e={r.e} /> },
              { title: 'Được hưởng', key: 'ent', width: 110, align: 'right', render: (_, r) => <Tooltip title={r.b.seniority ? `Gồm ${r.b.seniority} ngày thâm niên` : undefined}>{r.b.entitled}</Tooltip> },
              { title: 'Năm trước', key: 'car', width: 100, align: 'right', render: (_, r) => r.b.carried || <span className="text-gray-300">—</span> },
              { title: 'Đã nghỉ', key: 'used', width: 90, align: 'right', render: (_, r) => r.b.used },
              { title: 'Chờ duyệt', key: 'pend', width: 100, align: 'right', render: (_, r) => (r.pending ? <span className="text-amber-600">{r.pending}</span> : <span className="text-gray-300">—</span>) },
              { title: 'Còn lại', key: 'remain', width: 90, align: 'right', render: (_, r) => <b className={r.b.remain <= 0 ? 'text-rose-600' : r.b.remain <= 2 ? 'text-amber-600' : 'text-emerald-600'}>{r.b.remain}</b> },
              {
                title: 'Ghi chú', key: 'note', width: 230, render: (_, r) => (
                  <div className="text-xs text-gray-500">
                    {r.kind === 'service' && <div>Cộng tác viên — không có phép năm</div>}
                    {r.b.probation && <div>Đang thử việc</div>}
                    {r.next && <div>Sắp nghỉ: {LEAVE_KIND[r.next.kind].label} {fmtD(r.next.from)}</div>}
                  </div>
                ),
              },
            ]} />
        </div>
      </div>
    </Layout>
  );
}
